import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { useMutation } from "@apollo/client";
import Icon, { ICON_TYPE, ICON_SIZE } from "components/Icon";
import { colors, margins } from "style";
import { EditButton, SecondaryButton } from "components/Buttons";

/**
 * Create New Instance
 *
 * @params {Array} fields
 * @params {Function} refetch
 * @params {String} placeholder
 * @params {Object} queryName
 * @params {Object} additionalValues
 * @params {String} labelKey
 * @params {Function} onComplete
 * @params {Object} variables
 * @params {Array} templateVariables - Optional array of template variables for insertion
 *
 */
const CreateNewInstance = ({
  fields = [],
  queryName,
  additionalValues = {},
  refetch,
  labelKey,
  onComplete,
  variables = {},
  templateVariables = [],
  initiallyExpanded = false,
}) => {
  const [showInput, setShowInput] = useState(initiallyExpanded);
  const [handleCreateInstance, { loading }] = useMutation(queryName);
  const [fieldValues, setFieldValues] = useState({});
  const [cursorPosition, setCursorPosition] = useState(null);
  const inputRef = useRef(null);

  const handleFieldChange = (name, value) => setFieldValues({ ...fieldValues, [name]: value });
  const handleCursorChange = (event) => setCursorPosition(event.target.selectionStart);

  const handleTemplateInsert = (templateKey) => {
    const input = inputRef.current;
    if (!input) return;

    const fieldName = fields[0]?.name; // Assumes first field is the main input
    if (!fieldName) return;

    const currentValue = fieldValues[fieldName] || "";
    const position = cursorPosition !== null ? cursorPosition : currentValue.length;

    const newValue = currentValue.slice(0, position) + `{{${templateKey}}}` + currentValue.slice(position);

    handleFieldChange(fieldName, newValue);

    // Set cursor position after the inserted template
    setTimeout(() => {
      const newPosition = position + templateKey.length + 4; // 4 for {{ and }}
      input.handleSelectionChange?.(newPosition, newPosition);
      input.handleFocus?.();
      setCursorPosition(newPosition);
    }, 0);
  };

  const handleSubmit = (event) => {
    handleCreateInstance({
      variables: {
        input: {
          params: {
            ...additionalValues,
            ...fieldValues,
          },
          ...variables,
        },
      },
      onCompleted: (data) => {
        onComplete?.(data);
        setShowInput(false);
        setFieldValues({});
        refetch();
      },
    });
    event.preventDefault();
  };

  if (!showInput) {
    return (
      <div css={styles.container}>
        <EditButton onClick={() => setShowInput(true)}>
          <FormattedMessage id={labelKey} />
        </EditButton>
      </div>
    );
  }

  return (
    <div>
      <div css={[styles.container, styles.form_container]}>
        {fields.map((field, index) => (
          <field.type
            key={field.name}
            ref={index === 0 ? inputRef : null}
            name={field.name}
            type={field.type}
            value={fieldValues[field.name]}
            placeholder={field.properties.placeholder}
            autoFocus={field.properties.autoFocus}
            onChange={handleFieldChange}
            onSelect={handleCursorChange}
            onClick={handleCursorChange}
            onKeyUp={handleCursorChange}
            {...field.properties}
          />
        ))}
        <SecondaryButton onClick={handleSubmit} disabled={loading}>
          <Icon type={ICON_TYPE.plus} size={ICON_SIZE.large} color={"inherit"} />
        </SecondaryButton>
      </div>
      {templateVariables?.length > 0 && (
        <div css={styles.template_variables_container}>
          <div css={styles.template_label}>
            <FormattedMessage id="CreateNewInstance.TemplateVariables.Label" />
          </div>
          <div css={styles.template_tags}>
            {templateVariables.map((template) => (
              <button
                key={template.key}
                type="button"
                css={styles.template_tag}
                onClick={() => handleTemplateInsert(template.key)}
              >
                {template.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: css`
    margin-left: -5rem;
    margin-right: -5rem;
    padding: 2rem 5rem;
    padding-bottom: 0;
  `,
  form_container: css`
    background: ${colors.grayAnatomyLight5};
    display: flex;
    flex-direction: row-reverse;
    gap: 1rem;
  `,
  icon: css`
    border-radius: 50%;
    width: 4rem;
    height: 3.5rem;
    background: ${colors.red};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.7rem;
  `,
  template_variables_container: css`
    display: flex;
    align-items: baseline;
    gap: ${margins.xxsmall};
    padding: 1rem 5rem;
    margin-left: -5rem;
    margin-right: -5rem;
    background: ${colors.grayAnatomyLight5};
    border-bottom: 1px solid ${colors.grayAnatomyLight3};
  `,
  template_label: css`
    font-size: 1.3rem;
    font-weight: 500;
    color: ${colors.grayAnatomy};
    margin-bottom: 0.8rem;
    min-width: 145px;
  `,
  template_tags: css`
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
  `,
  template_tag: css`
    background: ${colors.white};
    border: 1px solid ${colors.grayAnatomyLight3};
    border-radius: 0.4rem;
    padding: 0.6rem 1.2rem;
    font-size: 1.2rem;
    color: ${colors.primaryBlue};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: ${colors.primaryBlue};
      color: ${colors.purpleRainBase};
      border-color: ${colors.purpleRainLight6};
    }

    &:active {
      transform: scale(0.98);
    }
  `,
};

CreateNewInstance.propTypes = {
  fields: PropTypes.array.isRequired,
  refetch: PropTypes.func,
  queryName: PropTypes.object,
  additionalValues: PropTypes.object,
  placeholder: PropTypes.string,
  labelKey: PropTypes.string,
  onComplete: PropTypes.func,
  keyName: PropTypes.string,
  variables: PropTypes.object,
  templateVariables: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  initiallyExpanded: PropTypes.bool,
};

export default CreateNewInstance;
